import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaArrowLeft, FaBook, FaClipboardCheck, FaClock, FaTrophy, FaSpinner, FaCalendarAlt, FaFileAlt, FaStar } from 'react-icons/fa';
import axios from 'axios';
import config from '../config/api';

const StudentProgress = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userType } = useAuth();
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        setLoading(true);
        
        // Se for um professor, busca progresso de um estudante específico
        // Se for um estudante, busca seu próprio progresso
        let endpoint;
        if (userType === 'teacher') {
          endpoint = `${config.apiURL}/teacher/student/${id}/progress`;
        } else if (userType === 'student') {
          endpoint = `${config.apiURL}/student/progress`;
        } else {
          throw new Error('Acesso não autorizado');
        }

        const response = await axios.get(endpoint);
        setProgressData(response.data);
      } catch (err) {
        console.error('Erro ao carregar progresso:', err);
        setError('Erro ao carregar dados de progresso');
      } finally {
        setLoading(false);
      }
    };

    fetchProgress();
  }, [id, userType]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Enviado':
        return 'bg-blue-100 text-blue-800';
      case 'Corrigido':
        return 'bg-green-100 text-green-800';
      case 'Em Aberto':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getGradeColor = (grade) => {
    if (grade >= 8) return 'text-green-600';
    if (grade >= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <FaSpinner className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Carregando progresso...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Erro</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <FaArrowLeft className="mr-2" />
            Voltar
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Progresso do Estudante
              </h1>
              <p className="mt-2 text-gray-600">
                Acompanhe o desempenho e progresso nas atividades
              </p>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FaBook className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total de Lições
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {progressData?.total_homeworks || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FaClipboardCheck className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Enviadas
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {progressData?.completed_submissions || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FaTrophy className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Corrigidas
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {progressData?.graded_submissions || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FaStar className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Média
                    </dt>
                    <dd className={`text-lg font-medium ${getGradeColor(progressData?.average_grade || 0)}`}>
                      {progressData?.average_grade || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Submissions List */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Histórico de Atividades
            </h3>
          </div>
          
          <div className="overflow-hidden">
            {progressData?.submissions && progressData.submissions.length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {progressData.submissions.map((submission, index) => (
                  <li key={index} className="px-6 py-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900">
                            {submission.homework_title}
                          </p>
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(submission.status)}`}>
                              {submission.status}
                            </span>
                            {submission.grade && (
                              <span className={`text-sm font-semibold ${getGradeColor(submission.grade)}`}>
                                Nota: {submission.grade}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="mt-2 flex items-center text-sm text-gray-500 space-x-4">
                          <div className="flex items-center">
                            <FaFileAlt className="mr-1 h-4 w-4" />
                            {submission.homework_subject}
                          </div>
                          <div className="flex items-center">
                            <FaCalendarAlt className="mr-1 h-4 w-4" />
                            Prazo: {formatDate(submission.due_date)}
                          </div>
                          {submission.submitted_at && (
                            <div className="flex items-center">
                              <FaClock className="mr-1 h-4 w-4" />
                              Enviado: {formatDate(submission.submitted_at)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-6 py-12 text-center">
                <FaBook className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  Nenhuma atividade encontrada
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Este estudante ainda não possui atividades atribuídas.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentProgress; 